import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router';
import { Badge } from '@/components/shadcn/badge';
import Table from '@/components/UI/Table';
import { DATE_FORMAT } from '@/constants';
import { useApplicationsListContext } from '@/features/ApplicationForm/Components/ApplicationsList/ApplicationsListProvider';
import { convertEnumToText } from '@/utils';
import type { GmfApplicationSummary } from './types/gmfDeveloperPipeline.types';
import { parseMetricNumber } from './utils/dashboardMetrics';
import { formatCurrency, formatNumber } from './utils/formatters';

export const GMFDeveloperPipelineApplicationsList = () => {
  const { data, tablePagination } = useApplicationsListContext();
  const navigate = useNavigate();
  const location = useLocation();

  const tableColumns: ColumnDef<GmfApplicationSummary>[] = [
    {
      id: 'developer',
      header: 'Developer',
      accessorKey: 'applicationData',
      size: 260,
      cell: ({ row }) => {
        const applicant = row.original.applicationData?.applicantInformation;
        return (
          <div className='flex max-w-md flex-col text-sm'>
            <span className='truncate font-medium text-slate-800'>{applicant?.entityName || row.original.contactName}</span>
            <span className='truncate text-xs text-slate-500'>{applicant?.contactPersonEmail || row.original.contactEmail}</span>
          </div>
        );
      },
    },
    {
      id: 'project',
      header: 'Proposed Property',
      accessorKey: 'applicationData',
      size: 260,
      cell: ({ row }) => {
        const project = row.original.applicationData?.projectDetails;
        return (
          <div className='flex flex-col gap-1 text-sm'>
            <span className='font-medium text-slate-800'>{project?.streetAddress || 'Address not provided'}</span>
            <span className='text-xs text-slate-500'>Milwaukee, Wisconsin {project?.zipCode}</span>
          </div>
        );
      },
    },
    {
      id: 'units',
      header: 'Units',
      accessorKey: 'applicationData.projectDetails.proposedUnits',
      size: 120,
      cell: ({ row }) => formatNumber(parseMetricNumber(row.original.applicationData?.projectDetails?.proposedUnits)),
    },
    {
      id: 'developmentCost',
      header: 'Development Cost',
      accessorKey: 'applicationData.projectDetails.developmentCostEstimate',
      size: 190,
      cell: ({ row }) => formatCurrency(parseMetricNumber(row.original.applicationData?.projectDetails?.developmentCostEstimate)),
    },
    {
      id: 'siteControl',
      header: 'Site Control',
      accessorKey: 'applicationData.projectDetails.siteControlStatus',
      size: 200,
    },
    {
      id: 'acreGraduate',
      header: 'ACRE Grad',
      accessorKey: 'applicationData.applicantInformation.acreGraduate',
      size: 120,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 180,
      cell: ({ row }) => <Badge variant='outline'>{convertEnumToText(row.original.status ?? '')}</Badge>,
    },
    {
      id: 'createdAt',
      header: 'Submitted',
      accessorKey: 'createdAt',
      size: 180,
      cell: ({ getValue }) => {
        const value = getValue();
        return typeof value === 'string' ? dayjs(value).format(DATE_FORMAT) : '';
      },
    },
  ];

  return (
    <Table
      cols={tableColumns}
      tableData={data?.data}
      onRowClick={(row: GmfApplicationSummary) => navigate(`${location.pathname}/${row.id}`)}
      pagination={tablePagination}
    />
  );
};
